"use client"

import { useState, useEffect, useRef } from "react"

import { useNavigate } from "react-router-dom"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Table from "../components/Table"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { Search, Filter, Eye } from "react-feather"

const Approvals = () => {
  const [approvals, setApprovals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [alert, setAlert] = useState(null)
  const navigate = useNavigate()
  const searchTimeout = useRef(null)
  const API_BASE_URL = 'https://api.tenarx.net';

  const fetchApprovals = async (page = 1, search = "", status = "all") => {
    try {
      setLoading(true)
      const token = localStorage.getItem("userToken")

      if (!token) {
        navigate("/")
        return
      }

      const url = new URL(`${API_BASE_URL}/api/auth/admin/all-pending`)
      url.searchParams.append("page", page)
      url.searchParams.append("limit", 10)
      if (search) url.searchParams.append("search", search)
      if (status && status !== "all") url.searchParams.append("status", status)
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch approvals")
      }

      const data = await response.json()
      setApprovals(data.data || [])
      setTotalPages(Math.ceil((data.total || 0) / 10))
    } catch (error) {
      setError(error.message)
      setAlert({
        type: "error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchApprovals(currentPage, searchTerm, statusFilter)
    // eslint-disable-next-line
  }, [currentPage, statusFilter, navigate])

  const handleSearch = () => {
    setCurrentPage(1)
    fetchApprovals(1, searchTerm, statusFilter)
  }

  const handleFilterChange = (e) => {
    const status = e.target.value
    setStatusFilter(status)
    setCurrentPage(1)
    // fetchApprovals(1, searchTerm, status) // handled by useEffect
  }

  const handleSearchInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    setCurrentPage(1)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      fetchApprovals(1, value, statusFilter)
    }, 400)
  }

  const handleViewClick = (userId) => {
    navigate(`/pending-account/${userId}`)
  }

  const columns = [
    {
      header: "No",
      accessor: "",
      render: (row, index) => (currentPage - 1) * 10 + index + 1,
      style: { width: "50px" },
    },
    {
      header: "Email",
      accessor: "email",
    },
    {
      header: "Role",
      accessor: "role",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
          {row.role}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "accountStatus",
      render: (row) => {
        let statusClass = ""
        switch (row.accountStatus) {
          case "pending":
          case "pending_approval":
            statusClass = "bg-yellow-100 text-yellow-800"
            break
          case "approved":
          case "active":
            statusClass = "bg-green-100 text-green-800"
            break
          case "rejected":
            statusClass = "bg-red-100 text-red-800"
            break
          default:
            statusClass = "bg-gray-100 text-gray-800"
        }

        return (
          <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${statusClass}`}>
            {row.accountStatus?.replace("_", " ")}
          </span>
        )
      },
    },
    {
      header: "Info Submitted",
      accessor: "personalInfoSubmitted",
      render: (row) => <span>{row.personalInfoSubmitted ? "Yes" : "No"}</span>,
    },
    {
      header: "Actions",
      accessor: "",
      render: (row) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleViewClick(row._id)}
          className="flex items-center gap-1"
        >
          <Eye className="w-4 h-4" />
          View
        </Button>
      ),
    },
  ]

  return (
    <Layout title="Approval Requests">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search by email..."
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="flex items-center">
              <Filter className="w-4 h-4 text-gray-500 mr-2" />
              <select className="border rounded-lg px-4 py-2" value={statusFilter} onChange={handleFilterChange}>
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <Button onClick={handleSearch}>Search</Button>
        </div>

        <Table columns={columns} data={approvals} isLoading={loading} emptyMessage="No approval requests found" />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex rounded-md shadow">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border-t border-b border-gray-300 bg-white text-sm font-medium ${
                      currentPage === pageNum
                        ? "bg-blue-50 text-blue-600 border-blue-500"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </Card>
    </Layout>
  )
}

export default Approvals
